import { FC, Fragment, ReactNode, useCallback, useContext, useEffect, useState } from "react";

import { Box, Grid, Stack, useTheme } from "@mui/material";
import { AvailablePaymentCurrency, getLocalesFromIsoLocale, RouteBuilders } from "@simplyk/common";
import InfiniteScroll from "react-infinite-scroll-component";

import { FundraiserCardSkeleton } from "@/canvas/features/FormV2/blocks/peer-to-peer/FundraiserCardSkeleton";
import {
  GeneralFundraiserCard,
  GeneralFundraiserCardProps,
} from "@/canvas/features/FormV2/blocks/peer-to-peer/GeneralFundraiserCard";
import {
  IndividualFundraiserCard,
  IndividualFundraiserCardProps,
} from "@/canvas/features/FormV2/blocks/peer-to-peer/IndividualFundraiserCard";
import {
  TeamFundraiserCard,
  TeamFundraiserCardProps,
} from "@/canvas/features/FormV2/blocks/peer-to-peer/TeamFundraiserCard";
import { removeCampaignNavigationSearchParams } from "@/canvas/features/FormV2/helpers/campaignUrls";
import { FrontendFormContext } from "@/common/contexts/FrontendFormContext";
import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { trpc } from "@/common/helpers/trpc";
import { appendQueryParams } from "@/common/helpers/url";
import { useMediaQuery } from "@/common/hooks/useMediaQuery";
import { Trans, useTranslate } from "@/common/hooks/useTranslate";
import { useUrlBuilder } from "@/common/hooks/useUrlBuilder";
import { FormDialog, FormDialogProps } from "@/forms-design-system/FormDialog";
import { FormSearchField } from "@/forms-design-system/FormSearchField";
import { FormTypography } from "@/forms-design-system/FormTypography";

type CampaignType = { type: "general" | "team" | "individual" };
export type SearchedCampaign =
  | (GeneralFundraiserCardProps & CampaignType)
  | (TeamFundraiserCardProps & CampaignType)
  | (IndividualFundraiserCardProps & CampaignType);

export type FundraisersInformationDialogProps = Omit<
  StatelessFundraisersInformationDialogProps,
  | "isLoading"
  | "generalCampaign"
  | "teamCampaigns"
  | "totalTeamCampaigns"
  | "hasMoreTeamCampaigns"
  | "onFetchMoreTeamCampaigns"
  | "individualCampaigns"
  | "totalIndividualCampaigns"
  | "hasMoreIndividualCampaigns"
  | "onFetchMoreIndividualCampaigns"
  | "searchedCampaigns"
  | "hasMoreSearchedCampaigns"
  | "onFetchMoreSearchedCampaigns"
> & {
  campaignId: string;
  formCurrency: AvailablePaymentCurrency;
};

export const FundraisersInformationDialog: FC<FundraisersInformationDialogProps> = ({
  open,
  search,
  onSearchChange,
  campaignId,
  mode,
  formCurrency,
  ...props
}) => {
  const { isoLocale } = useLocaleContext();
  const { isEmbedV2 } = useContext(FrontendFormContext);
  const [internalSearch, setInternalSearch] = useState(search);
  const [internalMode, setInternalMode] = useState(mode);
  const urlBuilder = useUrlBuilder();

  useEffect(() => {
    // Only update the internal mode when the dialog is open, so the dialog doesn't render differently when closing
    if (open) {
      setInternalMode(mode);
    }
  }, [mode, open]);

  useEffect(() => {
    if (!open) {
      setInternalSearch(search);
    }
  }, [search, open]);

  const handleOnSearchChange = useCallback(
    (search: string) => {
      setInternalSearch(search);
      onSearchChange(search);
    },
    [onSearchChange]
  );

  const { data: generalCampaignData, isFetching: isGeneralCampaignFetching } =
    trpc.form_getPrimaryPeerToPeerCampaign.useQuery(
      {
        campaignId,
        locale: getLocalesFromIsoLocale(isoLocale),
      },
      {
        enabled: open,
      }
    );

  const {
    data: teamCampaignsData,
    isFetching: isTeamCampaignsFetching,
    isFetchingNextPage: isTeamCampaignsFetchingNextPage,
    hasNextPage: hasMoreTeamCampaigns,
    fetchNextPage: fetchMoreTeamCampaigns,
  } = trpc.form_getTeamPeerToPeerCampaigns.useInfiniteQuery(
    {
      campaignId,
      limit: 10,
      locale: getLocalesFromIsoLocale(isoLocale),
    },
    {
      enabled: open,
      initialCursor: 0,
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
    }
  );

  const {
    data: individualCampaignsData,
    isFetching: isIndividualCampaignsFetching,
    isFetchingNextPage: isIndividualCampaignsFetchingNextPage,
    hasNextPage: hasMoreIndividualCampaigns,
    fetchNextPage: fetchMoreIndividualCampaigns,
  } = trpc.form_getIndividualPeerToPeerCampaigns.useInfiniteQuery(
    {
      campaignId,
      limit: 10,
      locale: getLocalesFromIsoLocale(isoLocale),
    },
    {
      enabled: open,
      initialCursor: 0,
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
    }
  );

  const {
    data: searchedCampaignsData,
    isFetching: isSearchedCampaignsFetching,
    isFetchingNextPage: isSearchedCampaignsFetchingNextPage,
    hasNextPage: hasMoreSearchedCampaigns,
    fetchNextPage: fetchMoreSearchedCampaigns,
  } = trpc.searchPeerToPeerCampaigns.useInfiniteQuery(
    {
      campaignId,
      limit: 10,
      search,
      locale: getLocalesFromIsoLocale(isoLocale),
    },
    {
      enabled: open && !!search,
      initialCursor: 0,
      getNextPageParam: (lastPage) => lastPage?.nextCursor,
    }
  );

  const generalCampaign = (() => {
    if (generalCampaignData) {
      const { path, ...data } = generalCampaignData;

      return {
        ...data,
        href: urlBuilder({
          route: RouteBuilders.buildPeerToPeerV2CampaignLink({
            type: "general",
            isoLocale,
            path,
            isEmbedV2,
          }),
          searchParams: removeCampaignNavigationSearchParams,
        }),
        currency: formCurrency,
      };
    }

    return null;
  })();

  const teamCampaigns =
    teamCampaignsData?.pages
      ?.filter((x) => !!x)
      ?.map((x) => x!.items)
      ?.flat()
      ?.map((x) => ({
        ...x,
        href: urlBuilder({
          route: RouteBuilders.buildPeerToPeerV2CampaignLink({
            type: "team",
            isoLocale,
            path: x.path,
            isEmbedV2,
          }),
          searchParams: removeCampaignNavigationSearchParams,
        }),
        currency: formCurrency,
      })) ?? [];

  const individualCampaigns =
    individualCampaignsData?.pages
      ?.filter((x) => !!x)
      ?.map((x) => x!.items)
      ?.flat()
      ?.map((x) => ({
        ...x,
        href: urlBuilder({
          route: RouteBuilders.buildPeerToPeerV2CampaignLink({
            type: "individual",
            isoLocale,
            path: x.path,
            isEmbedV2,
          }),
          searchParams: removeCampaignNavigationSearchParams,
        }),
        currency: formCurrency,
      })) ?? [];

  const searchedCampaigns =
    searchedCampaignsData?.pages
      ?.filter((x) => !!x)
      ?.map((x) => x!.items)
      ?.flat()
      ?.map((x) => ({
        ...x,
        href: urlBuilder({
          route: RouteBuilders.buildPeerToPeerV2CampaignLink({
            // When in embed V2, always use "embed" type, otherwise use the campaign's actual type
            type: x.type,
            isoLocale,
            path: x.path,
            isEmbedV2,
          }),
          searchParams: removeCampaignNavigationSearchParams,
        }),
        currency: formCurrency,
      })) ?? [];

  return (
    <StatelessFundraisersInformationDialog
      {...props}
      mode={internalMode}
      open={open}
      search={internalSearch}
      onSearchChange={handleOnSearchChange}
      isLoading={
        isGeneralCampaignFetching ||
        (isTeamCampaignsFetching && !isTeamCampaignsFetchingNextPage) ||
        (isIndividualCampaignsFetching && !isIndividualCampaignsFetchingNextPage) ||
        internalSearch !== search ||
        (isSearchedCampaignsFetching && !isSearchedCampaignsFetchingNextPage)
      }
      generalCampaign={generalCampaign}
      teamCampaigns={teamCampaigns}
      hasMoreTeamCampaigns={hasMoreTeamCampaigns ?? false}
      onFetchMoreTeamCampaigns={fetchMoreTeamCampaigns}
      individualCampaigns={individualCampaigns}
      hasMoreIndividualCampaigns={hasMoreIndividualCampaigns ?? false}
      onFetchMoreIndividualCampaigns={fetchMoreIndividualCampaigns}
      searchedCampaigns={searchedCampaigns}
      hasMoreSearchedCampaigns={hasMoreSearchedCampaigns ?? false}
      onFetchMoreSearchedCampaigns={fetchMoreSearchedCampaigns}
    />
  );
};

export type StatelessFundraisersInformationDialogProps = Pick<FormDialogProps, "open" | "onClose"> & {
  mode: "find-fundraiser-campaign" | "donate-to-campaign";
  isLoading?: boolean;
  search: string;
  onSearchChange: (search: string) => void;
  generalCampaign: GeneralFundraiserCardProps | null;
  teamCampaigns: TeamFundraiserCardProps[];
  individualCampaigns: IndividualFundraiserCardProps[];
  hasMoreTeamCampaigns: boolean;
  hasMoreIndividualCampaigns: boolean;
  onFetchMoreIndividualCampaigns: () => void;
  onFetchMoreTeamCampaigns: () => void;
  searchedCampaigns: SearchedCampaign[];
  hasMoreSearchedCampaigns: boolean;
  onFetchMoreSearchedCampaigns: () => void;
};

export const StatelessFundraisersInformationDialog: FC<StatelessFundraisersInformationDialogProps> = ({
  mode,
  isLoading = false,
  generalCampaign,
  teamCampaigns,
  individualCampaigns,
  search,
  onSearchChange,
  open,
  onClose,
  hasMoreTeamCampaigns,
  hasMoreIndividualCampaigns,
  onFetchMoreIndividualCampaigns,
  onFetchMoreTeamCampaigns,
  searchedCampaigns,
  hasMoreSearchedCampaigns,
  onFetchMoreSearchedCampaigns,
}) => {
  const { t } = useTranslate();
  const theme = useTheme();

  const { isSmallScreen } = useMediaQuery();

  const hasTeamCampaigns = isLoading || teamCampaigns.length > 0;
  const hasIndividualCampaigns = isLoading || individualCampaigns.length > 0;

  // On mobile, only show individual campaigns when all team campaigns have been loaded
  const showIndividualCampaigns = isSmallScreen
    ? hasIndividualCampaigns && !hasMoreTeamCampaigns
    : hasIndividualCampaigns;

  const handleFetchMoreCampaigns = useCallback(() => {
    if (isSmallScreen) {
      if (hasMoreTeamCampaigns) {
        onFetchMoreTeamCampaigns();
      } else {
        onFetchMoreIndividualCampaigns();
      }
    } else {
      onFetchMoreTeamCampaigns();
      onFetchMoreIndividualCampaigns();
    }
  }, [isSmallScreen, hasMoreTeamCampaigns, onFetchMoreTeamCampaigns, onFetchMoreIndividualCampaigns]);

  const renderSearchContent = () => {
    const infiniteScrollLoader = (
      <Stack gap={1} marginBlockStart={1}>
        {Array.from({ length: 3 }).map((_, index) => (
          <FundraiserCardSkeleton key={index} />
        ))}
      </Stack>
    );

    return (
      <InfiniteScroll
        dataLength={searchedCampaigns.length}
        next={onFetchMoreSearchedCampaigns}
        hasMore={hasMoreSearchedCampaigns}
        loader={infiniteScrollLoader}
        height={isSmallScreen ? "70dvh" : 460}
        style={{ padding: theme.spacing(3) }}
      >
        <Stack gap={3}>
          <FormSearchField
            value={search}
            onChange={onSearchChange}
            placeholder={t("donationForm", "peerToPeer.fundraisersDialog.searchPlaceholder")}
          />
          <Stack spacing={1}>
            {isLoading ? (
              Array.from({ length: 6 }).map((_, index) => <FundraiserCardSkeleton key={index} />)
            ) : searchedCampaigns.length > 0 ? (
              searchedCampaigns.map(({ type, ...campaign }, index) => (
                <Fragment key={index}>
                  {type === "general" && (
                    <GeneralFundraiserCard
                      {...(campaign as GeneralFundraiserCardProps)}
                      href={appendQueryParams(campaign.href, { donate: "true" })}
                    />
                  )}
                  {type === "team" && <TeamFundraiserCard {...(campaign as TeamFundraiserCardProps)} />}
                  {type === "individual" && (
                    <IndividualFundraiserCard {...(campaign as IndividualFundraiserCardProps)} />
                  )}
                </Fragment>
              ))
            ) : (
              <FormTypography variant="body2" vibe="supershy">
                <Trans
                  space="donationForm"
                  i18nKey="peerToPeer.fundraisers.searchNoResults"
                  components={{ sb: <FormTypography variant="subtitle2" component="span" /> }}
                />
              </FormTypography>
            )}
          </Stack>
        </Stack>
      </InfiniteScroll>
    );
  };

  const renderFundraisersByType = () => {
    const renderLoadingFundraisers = (): ReactNode => (
      <Stack gap={1}>
        {Array.from({ length: 3 }).map((_, index) => (
          <FundraiserCardSkeleton key={index} />
        ))}
      </Stack>
    );

    const infiniteScrollLoader = (
      <Box marginBlockStart={1}>
        {isSmallScreen ? (
          renderLoadingFundraisers()
        ) : (
          <Grid container spacing={3}>
            <Grid item xs={12} md={hasIndividualCampaigns ? 6 : 12}>
              {hasMoreTeamCampaigns && renderLoadingFundraisers()}
            </Grid>
            <Grid item xs={12} md={hasTeamCampaigns ? 6 : 12}>
              {hasMoreIndividualCampaigns && renderLoadingFundraisers()}
            </Grid>
          </Grid>
        )}
      </Box>
    );

    return (
      <InfiniteScroll
        dataLength={
          isSmallScreen
            ? teamCampaigns.length + individualCampaigns.length
            : Math.max(teamCampaigns.length, individualCampaigns.length)
        }
        next={handleFetchMoreCampaigns}
        hasMore={hasMoreTeamCampaigns || hasMoreIndividualCampaigns}
        loader={infiniteScrollLoader}
        height={isSmallScreen ? "70dvh" : 460}
        style={{ padding: theme.spacing(3) }}
      >
        <Stack gap={3}>
          {(hasTeamCampaigns || hasIndividualCampaigns) && (
            <FormSearchField
              value={search}
              onChange={onSearchChange}
              placeholder={t("donationForm", "peerToPeer.fundraisersDialog.searchPlaceholder")}
            />
          )}
          <Stack gap={1}>
            <FormTypography variant="subtitle1" vibe="moderate">
              {t("donationForm", "peerToPeer.fundraisersDialog.generalCampaignLabel")}
            </FormTypography>
            {isLoading ? (
              <FundraiserCardSkeleton />
            ) : (
              !!generalCampaign && (
                <GeneralFundraiserCard
                  {...generalCampaign}
                  href={appendQueryParams(generalCampaign.href, { donate: "true" })}
                  data-test="general-campaign-fundraiser"
                />
              )
            )}
          </Stack>
          <Grid container spacing={3}>
            {hasTeamCampaigns && (
              <Grid item xs={12} md={hasIndividualCampaigns ? 6 : 12}>
                <Stack gap={1}>
                  <FormTypography variant="subtitle1" vibe="moderate">
                    {t("donationForm", "peerToPeer.fundraisersDialog.teamCampaignsLabel")}
                  </FormTypography>
                  <Stack gap={1}>
                    {isLoading
                      ? Array.from({ length: 4 }).map((_, index) => <FundraiserCardSkeleton key={index} />)
                      : teamCampaigns.map((campaign, index) => <TeamFundraiserCard key={index} {...campaign} />)}
                  </Stack>
                </Stack>
              </Grid>
            )}
            {showIndividualCampaigns && (
              <Grid item xs={12} md={hasTeamCampaigns ? 6 : 12}>
                <Stack gap={1}>
                  <FormTypography variant="subtitle1" vibe="moderate">
                    {t("donationForm", "peerToPeer.fundraisersDialog.individualCampaignsLabel")}
                  </FormTypography>
                  <Stack gap={1}>
                    {isLoading
                      ? Array.from({ length: 4 }).map((_, index) => <FundraiserCardSkeleton key={index} />)
                      : individualCampaigns.map((campaign, index) => (
                          <IndividualFundraiserCard key={index} {...campaign} />
                        ))}
                  </Stack>
                </Stack>
              </Grid>
            )}
          </Grid>
        </Stack>
      </InfiniteScroll>
    );
  };

  const title = () => {
    return (
      <FormTypography variant="h6">
        {mode === "find-fundraiser-campaign"
          ? t("donationForm", "peerToPeer.fundraisersDialog.fundraisersTitle")
          : t("donationForm", "peerToPeer.fundraisersDialog.donateToCampaignTitle")}
      </FormTypography>
    );
  };

  return (
    <FormDialog
      title={title()}
      open={open}
      onClose={onClose}
      noPadding
      size="s"
      variant={isSmallScreen ? "drawer" : "default"}
    >
      {search ? renderSearchContent() : renderFundraisersByType()}
    </FormDialog>
  );
};
